/**
 * Centralized API error parsing and user-friendly message generation.
 * Handles Django REST Framework and common HTTP error formats.
 */

/**
 * Parse an axios/API error into a user-friendly message.
 * @param {Error} error - Axios error object
 * @returns {{ message: string, statusCode?: number, fieldErrors?: Record<string, string> }}
 */
export function parseApiError(error) {
  const statusCode = error?.response?.status;
  const data = error?.response?.data;

  // Network error (no response from server)
  if (!error.response) {
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      return {
        message: "errors.timeout",
        statusCode: null,
        fieldErrors: null,
      };
    }
    if (error.message === "Network Error" || error.code === "ERR_NETWORK") {
      return {
        message: "errors.network",
        statusCode: null,
        fieldErrors: null,
      };
    }
    return {
      message: "errors.connection",
      statusCode: null,
      fieldErrors: null,
    };
  }

  // Parse Django REST Framework / backend error formats
  if (data && typeof data === "object") {
    // 1. Simple detail string
    if (typeof data.detail === "string") {
      return {
        message: data.detail,
        statusCode,
        fieldErrors: null,
      };
    }
    // 2. Detail as array (DRF sometimes returns this)
    if (Array.isArray(data.detail)) {
      const msg = data.detail.map((d) => (typeof d === "object" ? d.msg || JSON.stringify(d) : d)).join(". ");
      return {
        message: msg || "errors.generic",
        statusCode,
        fieldErrors: null,
      };
    }
    // 3. non_field_errors
    if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
      return {
        message: data.non_field_errors.join(". "),
        statusCode,
        fieldErrors: null,
      };
    }
    // 4. Field-specific errors (e.g. { patient: ["This field is required."], content: ["Cannot be blank."] })
    const fieldErrors = {};
    let firstFieldMessage = null;
    for (const [key, value] of Object.entries(data)) {
      if (key === "detail" || key === "non_field_errors") continue;
      const msg = Array.isArray(value) ? value.join(". ") : String(value);
      fieldErrors[key] = msg;
      if (!firstFieldMessage) firstFieldMessage = msg;
    }
    if (Object.keys(fieldErrors).length > 0) {
      return {
        message: firstFieldMessage,
        statusCode,
        fieldErrors,
      };
    }
  }

  // Fallback: map by status code
  const statusMessages = {
    400: "errors.badRequest",
    401: "errors.unauthorized",
    403: "errors.forbidden",
    404: "errors.notFound",
    405: "errors.methodNotAllowed",
    408: "errors.timeout",
    409: "errors.conflict",
    422: "errors.validation",
    429: "errors.rateLimit",
    500: "errors.serverError",
    502: "errors.badGateway",
    503: "errors.serviceUnavailable",
  };

  return {
    message: statusMessages[statusCode] || "errors.generic",
    statusCode,
    fieldErrors: null,
  };
}

/**
 * Get the final display message, resolving i18n keys if needed.
 * @param {ReturnType<parseApiError>} parsed - Result from parseApiError
 * @param {Function} t - i18n t function
 * @returns {string}
 */
export function getDisplayMessage(parsed, t) {
  const key = parsed.message;
  const fallbackByKey = {
    "errors.network": "Connection problem. Please check your internet and backend server.",
    "errors.timeout": "Request timed out. Please try again.",
    "errors.connection": "Unable to connect to server. Please try again.",
    "errors.unauthorized": "Your session expired or you are not authorized. Please log in again.",
    "errors.forbidden": "You do not have permission to perform this action.",
    "errors.notFound": "Requested resource was not found.",
    "errors.serverError": "Server error occurred. Please try again later.",
    "errors.generic": "Something went wrong. Please try again.",
  };
  // If it looks like an i18n key (errors.xxx), translate it
  if (key && key.startsWith("errors.")) {
    const translated = t(key, { defaultValue: key });
    return translated !== key ? translated : (fallbackByKey[key] || fallbackByKey["errors.generic"]);
  }
  return key || t("errors.generic", { defaultValue: "Something went wrong. Please try again." });
}
